export type AnnouncementCopy = { title: string; body: string };
export type Announcement = AnnouncementCopy & {
    id: string;
    enabled: true;
    translations: Record<string, AnnouncementCopy>;
};

export const EBackResultCode = {
	SUCCESS: "success",
	ERROR: "error",
	ACTION_MISSING: "action_missing",
	ACTION_NOT_DEFINED: "action_not_defined",
	NEED_UPGRADE: "need_upgrade",
	NEED_LOGIN: "need_login",
	NEED_RECHARGE: "need_recharge",
	DAILY_CHECK_IN_ALREADY: "daily_check_in_already",
}

export const EReportType = {
    porn: "porn",
    violence: "violence",
    politics: "politics",
    illegal: "illegal",
    infringement: "infringement",
    other: "other",
}

export type BackResultCode = typeof EBackResultCode[keyof typeof EBackResultCode]

export interface BackResponse<T = unknown> {
    result_code: BackResultCode | string
    result?: T | string
    data?: T
    message?: string
}

export type UserProfile = {
    id: string;
    username: string;
    avatar: string | null;
    coins: number;
    solvePuzzleNum: number;
    likePuzzles: string[];
    puzzleChats: string[];
    is_subscribed?: boolean;
    subscription_source?: 'waffo' | 'app_store' | 'play_store' | 'revenuecat' | null;
    subscription_status?: 'active' | 'canceling' | 'past_due' | 'canceled' | 'expired' | null;
    subscription_current_period_end?: string | null;
    free_ai_create?: boolean;
    status?: 'normal' | 'wait_removal' | string;
}

export type Author = {
    id: string;
    username: string;
    avatar?: string | null;
}

export type Message = {
    id: string;
    content: string;
    role: 'user' | 'assistant' | 'system';
    timestamp: string;
    answer_type?: 'yes' | 'no' | 'both' | 'unrelated';
};

export type Puzzle = {
    id?: string;
    title: string;
    soup: string;
    truth?: string;
    keypoints?: string[];
    author: string;
    direct_author_name?: string;
    origin_type: 'original' | 'repost' | 'ai';
    likedNum?: number;
    chatNum?: number;
    created_at?: string;
    puzzleState?: 'draft' | 'pending' | 'approved' | 'rejected' | 'user_deleted' | 'author_only';
    themes?: string;
    is_pickup?: boolean;
    cover_url?: string | null;
    difficulty?: number | null;
}

export type PublicPuzzleTranslation = {
    title?: string;
    soup?: string;
};

/**
 * The only puzzle shape that may cross the public Server Component boundary.
 * Truth and keypoints are deliberately excluded so they cannot be serialized
 * into discover/detail HTML or the React Server Component payload.
 */
export type PublicPuzzle = Omit<Puzzle, 'id' | 'truth' | 'keypoints'> & {
    id: string;
    multi_lang_data?: Record<string, PublicPuzzleTranslation> | null;
};

export type PuzzleChat = {
    id: string | null;
    puzzle: Puzzle;
    timestamp: string;
    status: 'active' | 'completed' | 'revealed';
    messages: Message[];
    user_id: string;
    keypoints_state?: (boolean | null)[] | null;
    update_timestamp?: string | null;
}

export type Soup = {
    id: string;
    title: string;
    soup: string;
    truth: string;
    lastMessage: string;
    timestamp: string;
    unread: boolean;
    status: 'active' | 'completed';
    messages: Message[];
};

export type SPGTab = 'puzzles' | 'discover' | 'profile' | 'notifications';

export type SPGPage = 'list' | 'chat' | 'discover' | 'profile' | 'recharge' | 'settings' | 'createPuzzle' | 'notifications';

export const ENotificationType = {
    WELCOME: 'welcome',
    RECHARGE_SUCCESS: 'recharge_success',
    SUBSCRIPTION_BENEFIT_GRANTED: 'subscription_benefit_granted',
    PUZZLE_APPROVED: 'puzzle_approved',
    PUZZLE_REJECTED: 'puzzle_rejected',
    PUZZLE_LIKES_SUMMARY: 'puzzle_likes_summary',
    NEW_VERSION: 'new_version',
} as const;

export type UserNotification = {
    id: string;
    user_id: string;
    type: string;
    params: Record<string, unknown>;
    is_read: boolean;
    created_at: string;
};

export type PuzzleTab = 'latest' | 'hot-today' | 'hot-week';

export type PuzzleInteractionLog = {
    id: string;
    created_at: string;
    interactionType: 'like' | 'share' | 'chat' | 'mark';
    puzzle: string;
    user_id: string;
}
